import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Chip,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as VideoIcon,
  Article as ArticleIcon,
  Description as DocumentIcon,
  School as CourseIcon,
  MenuBook as BookIcon,
  Podcasts as PodcastIcon,
  Language as WebsiteIcon,
  Link as LinkIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Add as AddIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { KnowledgeNodeResource, ResourceType } from '../../types/knowledge';

interface ResourceListProps {
  resources: KnowledgeNodeResource[];
  nodeId: string;
  nodeName: string;
  onAddResource: () => void;
  onEditResource: (resource: KnowledgeNodeResource) => void;
  onDeleteResource: (resourceId: string) => void;
  onToggleFeatured: (resourceId: string, isFeatured: boolean) => void;
}

const resourceTypeIcons: Record<ResourceType, React.ReactElement> = {
  youtube_video: React.createElement(VideoIcon),
  article: React.createElement(ArticleIcon),
  document: React.createElement(DocumentIcon),
  course: React.createElement(CourseIcon),
  book: React.createElement(BookIcon),
  podcast: React.createElement(PodcastIcon),
  website: React.createElement(WebsiteIcon),
  other: React.createElement(LinkIcon),
};

const resourceTypeColors: Record<ResourceType, string> = {
  youtube_video: '#ff0000',
  article: '#1976d2',
  document: '#388e3c',
  course: '#f57c00',
  book: '#7b1fa2',
  podcast: '#d32f2f',
  website: '#0288d1',
  other: '#616161',
};

export function ResourceList({
  resources,
  nodeId,
  nodeName,
  onAddResource,
  onEditResource,
  onDeleteResource,
  onToggleFeatured,
}: ResourceListProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedResource, setSelectedResource] = useState<KnowledgeNodeResource | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent, resource: KnowledgeNodeResource) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget as HTMLElement);
    setSelectedResource(resource);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedResource(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (selectedResource) {
      onDeleteResource(selectedResource.id);
      setDeleteDialogOpen(false);
      setSelectedResource(null);
    }
  };

  const handleToggleFeatured = () => {
    if (selectedResource) {
      onToggleFeatured(selectedResource.id, !selectedResource.is_featured);
      handleMenuClose();
    }
  };

  const handleEditClick = () => {
    if (selectedResource) {
      onEditResource(selectedResource);
      handleMenuClose();
    }
  };

  const handleOpenResource = (resource: KnowledgeNodeResource) => {
    window.open(resource.url, '_blank', 'noopener,noreferrer');
  };

  const featuredResources = resources.filter(r => r.is_featured);
  const regularResources = resources.filter(r => !r.is_featured);

  const formatDuration = (duration: string) => {
    if (!duration) return null;
    // Basic duration formatting - could be enhanced
    return duration;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString();
  };

  const renderResourceCard = (resource: KnowledgeNodeResource) => (
    <Card
      key={resource.id}
      sx={{
        mb: 2,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        },
      }}
      onClick={() => handleOpenResource(resource)}
    >
      <Box sx={{ display: 'flex' }}>
        {/* Thumbnail */}
        <Box sx={{ width: 120, height: 120, flexShrink: 0 }}>
          {resource.thumbnail_url ? (
            <CardMedia
              component="img"
              image={resource.thumbnail_url}
              alt={resource.title}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: resourceTypeColors[resource.resource_type] + '20',
                color: resourceTypeColors[resource.resource_type],
              }}
            >
              {resourceTypeIcons[resource.resource_type]}
            </Box>
          )}
        </Box>

        {/* Content */}
        <CardContent sx={{ flexGrow: 1, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" component="h3" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              {resource.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {resource.is_featured && (
                <Tooltip title="Featured Resource">
                  <StarIcon color="warning" fontSize="small" />
                </Tooltip>
              )}
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, resource)}
                sx={{ ml: 1 }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {resource.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {resource.description}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip
              icon={resourceTypeIcons[resource.resource_type]}
              label={resource.resource_type.replace('_', ' ')}
              size="small"
              sx={{
                backgroundColor: resourceTypeColors[resource.resource_type] + '20',
                color: resourceTypeColors[resource.resource_type],
                border: `1px solid ${resourceTypeColors[resource.resource_type]}40`,
              }}
            />
            {resource.duration && (
              <Chip
                icon={<TimeIcon />}
                label={formatDuration(resource.duration)}
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: '0.75rem', color: 'text.secondary' }}>
            {resource.author && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon fontSize="small" />
                <span>{resource.author}</span>
              </Box>
            )}
            {resource.published_date && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarIcon fontSize="small" />
                <span>{formatDate(resource.published_date)}</span>
              </Box>
            )}
          </Box>

          {resource.tags.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {resource.tags.slice(0, 3).map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              ))}
              {resource.tags.length > 3 && (
                <Chip
                  label={`+${resource.tags.length - 3}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              )}
            </Box>
          )}
        </CardContent>
      </Box>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Resources ({resources.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAddResource}
          size="small"
        >
          Add Resource
        </Button>
      </Box>

      {/* Featured Resources */}
      {featuredResources.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon color="warning" />
            Featured Resources
          </Typography>
          {featuredResources.map(renderResourceCard)}
        </Box>
      )}

      {/* Regular Resources */}
      {regularResources.length > 0 && (
        <Box>
          {featuredResources.length > 0 && (
            <>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                All Resources
              </Typography>
            </>
          )}
          {regularResources.map(renderResourceCard)}
        </Box>
      )}

      {/* Empty State */}
      {resources.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No resources yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add educational resources like videos, articles, and courses to enhance this knowledge node.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddResource}
          >
            Add First Resource
          </Button>
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleToggleFeatured}>
          <ListItemIcon>
            {selectedResource?.is_featured ? (
              <StarBorderIcon fontSize="small" />
            ) : (
              <StarIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedResource?.is_featured ? 'Remove from Featured' : 'Mark as Featured'}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Resource</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedResource?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
